import { useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { LanguageToggle } from "@/components/LanguageToggle";
import { EmergencyAlarm } from "@/components/admin/EmergencyAlarm";
import { StaffPasswordGate } from "@/components/admin/StaffPasswordGate";
import {
    LayoutDashboard, CalendarCheck, Users, HardHat, Wrench, MapPin,
    Wallet, Receipt, AlertTriangle, Gift, Settings, LogOut, Menu, X, TrendingUp, Building2, Scale, ScrollText, Ticket, HeartHandshake, ShieldAlert, Siren, HeartPulse, Headphones, Store, UserCog, Inbox, Stethoscope, UserRoundCheck, BadgeIndianRupee, UserX,
} from "lucide-react";

const NAV = [
    { to: "/admin", label: "Dashboard", icon: LayoutDashboard, exact: true, key: "dashboard" },
    { to: "/admin/team-inbox", label: "Team Inbox", icon: Inbox, testid: "admin-nav-team-inbox", key: "inbox" },
    { section: "Home Services" },
    { to: "/admin/bookings", label: "Bookings", icon: CalendarCheck, key: "bookings" },
    { to: "/admin/demand", label: "Demand", icon: TrendingUp, key: "demand" },
    { to: "/admin/society-homecare", label: "Society HomeCare", icon: Building2, key: "society" },
    { to: "/admin/services", label: "Services & Areas", icon: Wrench, key: "services" },
    { section: "HOMIIE Care", care: true },
    { to: "/admin/emergencies", label: "Emergencies", icon: Siren, testid: "admin-nav-emergencies", danger: true, key: "emergencies" },
    { to: "/admin/care", label: "Care Dashboard", icon: HeartPulse, exact: true, testid: "admin-nav-care-dashboard", care: true, key: "care_dashboard" },
    { to: "/admin/care-visits", label: "Care Visits", icon: HeartHandshake, care: true, key: "care_visits" },
    { to: "/admin/healthcare", label: "Healthcare", icon: Stethoscope, testid: "admin-nav-healthcare", care: true, key: "healthcare" },
    { to: "/admin/caregivers", label: "Caregivers", icon: UserRoundCheck, testid: "admin-nav-caregivers", care: true, key: "caregivers" },
    { to: "/admin/care-payouts", label: "Care Payouts", icon: BadgeIndianRupee, testid: "admin-nav-care-payouts", care: true, key: "care_payouts" },
    { section: "People & Money" },
    { to: "/admin/customers", label: "Customers", icon: Users, key: "customers" },
    { to: "/admin/professionals", label: "Professionals", icon: HardHat, key: "professionals" },
    { to: "/admin/payments", label: "Payments", icon: Wallet, key: "payments" },
    { to: "/admin/vendors", label: "Vendors", icon: Store, testid: "admin-nav-vendors", key: "vendors" },
    { to: "/admin/refunds", label: "Refunds", icon: Receipt, testid: "admin-nav-refunds", key: "refunds" },
    { to: "/admin/account-closures", label: "Account Closures", icon: UserX, testid: "admin-nav-account-closures", key: "account_closures" },
    { to: "/admin/support", label: "Helpdesk", icon: Headphones, testid: "admin-nav-support", key: "support" },
    { to: "/admin/complaints", label: "Complaints", icon: AlertTriangle, key: "complaints" },
    { to: "/admin/safety", label: "Safety", icon: ShieldAlert, key: "safety" },
    { section: "Growth & Admin" },
    { to: "/admin/referrals", label: "Referrals", icon: Gift, key: "referrals" },
    { to: "/admin/offers", label: "Offers", icon: Ticket, key: "offers" },
    { to: "/admin/legal", label: "Legal & Policies", icon: Scale, key: "legal" },
    { to: "/admin/audit", label: "Audit Trail", icon: ScrollText, key: "audit" },
    { to: "/admin/settings", label: "Settings", icon: Settings, key: "settings" },
    { to: "/admin/team", label: "Team & Access", icon: UserCog, testid: "admin-nav-team", key: "master" },
];

const canSee = (user, key) => (!user?.is_staff ? true : key === "inbox" || (key !== "master" && (user.sections || []).includes(key)));
const sectionOf = (path) => {
    const hit = NAV.filter((n) => n.to && n.to !== "/admin" && (path === n.to || path.startsWith(`${n.to}/`))).sort((a, b) => b.to.length - a.to.length)[0];
    if (hit) { return hit.key; }
    return path.startsWith("/admin/bookings") ? "bookings" : "dashboard";
};

export function AdminLayout() {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    const loc = useLocation();
    const [open, setOpen] = useState(false);
    const doLogout = async () => { await logout(); nav("/"); };
    const items = NAV.filter((n) => n.section || canSee(user, n.key));
    const visible = items.filter((n, i) => !n.section || (items[i + 1] && !items[i + 1].section));
    const allowedHere = canSee(user, sectionOf(loc.pathname));
    const firstAllowed = NAV.find((n) => n.to && n.key !== "inbox" && canSee(user, n.key)) || NAV.find((n) => n.key === "inbox");

    const Side = (
        <div className="flex h-full flex-col bg-navy text-white">
            <div className="flex items-center justify-between px-5 py-5">
                <Logo size={32} light />
                <button className="lg:hidden" onClick={() => setOpen(false)}><X size={20} /></button>
            </div>
            <nav className="flex-1 space-y-1 overflow-y-auto px-3">
                {visible.map((n) => {
                    if (n.section) return <p key={n.section} className={`px-3 pb-1 pt-4 text-[10px] font-bold uppercase tracking-widest ${n.care ? "text-[#FB7185]" : "text-white/35"}`}>{n.section}</p>;
                    const Icon = n.icon;
                    return (
                        <NavLink
                            key={n.to}
                            to={n.to}
                            end={n.exact}
                            onClick={() => setOpen(false)}
                            data-testid={n.testid || `admin-nav-${n.label.split(" ")[0].toLowerCase()}`}
                            className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold transition-colors ${isActive ? (n.danger ? "bg-red-600 text-white" : n.care ? "bg-[#F43F5E] text-white" : "bg-orange text-white") : n.danger ? "text-red-300 hover:bg-red-500/20" : "text-white/70 hover:bg-white/10"}`}
                        >
                            <Icon size={18} /> {n.label}
                        </NavLink>
                    );
                })}
            </nav>
            <div className="border-t border-white/10 p-3">
                <div className="mb-2 px-3"><LanguageToggle dark /></div>
                <div className="mb-2 px-3 text-xs text-white/50" data-testid="admin-user-role">{user?.name} · {user?.is_staff ? `${(user.team || "staff").replace("_", " ")} team` : "Master admin"}</div>
                <button data-testid="admin-logout-btn" onClick={doLogout} className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold text-white/70 transition-colors hover:bg-white/10"><LogOut size={18} /> Log out</button>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-lightbg">
            <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 lg:block">{Side}</aside>
            {open && <div className="fixed inset-0 z-40 bg-black/40 lg:hidden" onClick={() => setOpen(false)} />}
            <aside className={`fixed inset-y-0 left-0 z-50 w-64 transition-transform lg:hidden ${open ? "translate-x-0" : "-translate-x-full"}`}>{Side}</aside>

            <div className="lg:pl-64">
                <header className="sticky top-0 z-20 flex items-center gap-3 border-b border-navy/10 bg-white px-4 py-3 lg:hidden">
                    <button onClick={() => setOpen(true)}><Menu size={22} className="text-navy" /></button>
                    <Logo size={28} />
                </header>
                <main className="p-4 lg:p-8">
                    {user?.is_staff && user.force_password_reset ? <StaffPasswordGate /> : allowedHere ? <Outlet />
                        : loc.pathname === "/admin" && firstAllowed ? <Navigate to={firstAllowed.to} replace />
                            : <div data-testid="admin-no-access" className="rounded-2xl border border-navy/10 bg-white p-8 text-center text-sm text-navy/60">Your team doesn't have access to this section. Ask the Master admin if you need it.</div>}
                    {canSee(user, "emergencies") && <EmergencyAlarm />}
                </main>
            </div>
        </div>
    );
}
